import React, { useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import 'dayjs/locale/en-gb'

import Button from '/react4xp/components/Dashboard/Button'
import Checkbox from '/react4xp/components/Dashboard/Checkbox'
import Accordion from '/react4xp/components/Dashboard/Accordion'
import Input from '/react4xp/components/Dashboard/Input'
import Card from '/react4xp/components/Dashboard/Card'
import Popup from '/react4xp/components/Dashboard/Popup'
import TextArea from '/react4xp/components/Dashboard/TextArea'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { nnNO } from '@mui/material/locale'

import { useDomLoaded } from '/lib/reactutils/utils'

// prop types
interface DashboardStatisticsProps {
    filters: {
        status: { id: string, text: string }[]
        packaging: { id: string, text: string }[]
        taste?: { id: string, text: string }[]
        category: { id: string, text: string }[]
        region?: { id: string, text: string }[]
        justShow?: { id: string, text: string }[]
        ingredients1Label?: string
        ingredients1?: { id: string, text: string }[]
        ingredients2Label?: string
        ingredients2?: { id: string, text: string }[]
        ingredients3Label?: string
        ingredients3?: { id: string, text: string }[]
    }
    endpoints: {
        statistics: string
        saveSearch: string
        csvDownload: string
        websocket: string
    }
    typePage: string
    strings: {
        saveSearchButton: string//Lagre søk
        saveSearchCreateHeading: string//Lagre søk
        saveSearchNameInputLabel: string//Navn
        saveSearchCancelButton: string//Avbryt
        saveSearchConfirmButton: string//Lagre

        saveSearchSuccessTitle: string//Success
        saveSearchSuccessContent: string//The search was created successfully
        saveSearchErrorTitle: string//Success
        saveSearchErrorContent: string//The search was created successfully

        editSearchButton: string//Endre
        editSearchTitle: string//Endre
        editSearchInputText: string//Navn
        editSearchConfirm: string//Lagre
        editSearchCancel: string//Avbryt

        popupErrorTitle: string//Error
        popupErrorContent: string//An error occurred during the action

        deleteSearchTitle: string//Ekskluder dette søket
        deleteSearchContent: string//Er du sikker på at du vil slette dette lagrede søket?
        deleteSearchConfirm: string//Ja
        deleteSearchCancel: string//Avbryt

        popupModalClose: string//Lukk

        saveSearchInputPlaceholder: string //Bringebær & lakris
        saveSearchShowAll: string //Vis lagrede søk
        saveSearchHeader: string //Lagrede søk
        clearAllFilters: string //Tøm alle filtre
        justShowFilterHeader: string //Vis bare
        dateFilterFrom: string //Fra
        dateFilterTo: string //Til
        dateFilterMaxHigherThanToday: string //Du må velge en dato som er mindre enn eller lik i dag
        statusFilterHeader: string //Status
        packagingFilterHeader: string //Innpakning
        tasteFilterHeader: string //Smak
        categoryFilterHeader: string //Kategori
        regionFilterHeader: string //Område
        chooseOne: string //Velg

        dateFilterClear: string //Klar

        searchedTerm: string//Searched term
    }
}

export default function DashboardStatistics({
    filters,
    endpoints,
    typePage,
    strings
}: DashboardStatisticsProps) {
    const domLoaded = useDomLoaded()

    const dateStart = useRef(null)
    const dateEnd = useRef(null)

    // handle cards
    const [cards, setCards] = useState([])
    const [filterDateStart, setFilterDateStart] = useState(null)
    const [filterDateEnd, setFilterDateEnd] = useState(null)
    const [editSearch, setEditSearch] = useState(null)
    const [inputValue, setInputValue] = useState('')
    const [searchQuery, setSearchQuery] = useState(null)

    const getQueryParams = () => {
        const queryParams = new URLSearchParams()

        const currentParams = window.location.search && new URLSearchParams(window.location.search)
        if (currentParams && currentParams.get('id')) {
            queryParams.append('id', currentParams.get('id'))
        }

        activeFilters.forEach(af => {
            if (af.name == 'status') queryParams.append('status', af.id)
            if (af.name == 'packaging') queryParams.append('packaging', af.id)
            if (af.name == 'taste') queryParams.append('taste', af.id)
            if (af.name == 'category') queryParams.append('category', af.id)
            if (af.name == 'region') queryParams.append('region', af.id)
            if (af.name == 'just-show') queryParams.append('just-show', af.id)

            if (af.name == 'ingredient1') queryParams.append('ingredient', af.id)
            if (af.name == 'ingredient2') queryParams.append('ingredient', af.id)
            if (af.name == 'ingredient3') queryParams.append('ingredient', af.id)
        })

        if (filterDateStart?.$d) queryParams.append('dateStart', dayjs(filterDateStart.$d.toISOString()).set('hour', 0).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss'))
        if (filterDateEnd?.$d) queryParams.append('dateEnd', dayjs(filterDateEnd.$d.toISOString()).set('hour', 23).set('minute', 59).set('second', 59).format('YYYY-MM-DDTHH:mm:ss'))

        if (searchQuery) queryParams.append('search', searchQuery)

        return queryParams
    }

    const handleCards = async () => {
        if (!endpoints.statistics) return

        const url = new URL(endpoints.statistics)
        const queryParams = getQueryParams()

        url.search = queryParams.toString()

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`)
            }
            const data = await response.json()

            setCards(data.cards || [])
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error)
        }
    }

    // state to track selected filters
    const [activeFilters, setActiveFilters] = useState([])

    useEffect(() => {
        handleCards()
    }, [activeFilters, filterDateStart, filterDateEnd])

    // handle checkbox changes
    const handleCheckboxChange = (id: string, text: string, name: string, checked: boolean) => {
        if (checked) {
            setActiveFilters((prevFilters) => [...prevFilters, { id, text, name }])
        } else {
            setActiveFilters((prevFilters) =>
                prevFilters.filter((filter) => filter.id !== id)
            )
        }
    }

    // clear a specific filter
    const clearFilter = (id: string) => {

        // remove the filter from the activeFilters array
        setActiveFilters((prevFilters) =>
            prevFilters.filter((filter) => filter.id !== id)
        )

        // uncheck the corresponding checkbox by its ID
        const checkbox = document.getElementById(id) as HTMLInputElement

        if (checkbox) {
            checkbox.checked = false
        }
    }

    const clearActiveFilters = () => {
        activeFilters.forEach(af => {
            const checkbox = document.getElementById(af.id) as HTMLInputElement
            if (checkbox) {
                checkbox.checked = false
            }
        })

        setActiveFilters([])
    }

    // show / hide the search bar saved searches
    const [searchFocus, setSearchFocus] = useState(false)

    // open popups
    useEffect(() => {
        const dialogTriggers = document.querySelectorAll('[data-dialog]')

        dialogTriggers.forEach(trigger => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault()

                document.body.classList.add('no-scroll')

                const dialogId = trigger.getAttribute('href').substring(1)
                const dialog = document.getElementById(dialogId)

                if (dialog && (dialog as HTMLDialogElement).showModal) {
                    (dialog as HTMLDialogElement).showModal()
                }
            })
        })
    }, [])

    // Save search
    const [searches, setSearches] = useState([])
    const [saveSearchLabel, setSaveSearchLabel] = useState('')
    const handleSaveSearch = () => {
        fetch(endpoints.saveSearch, {
            method: 'post',
            body: JSON.stringify({
                label: saveSearchLabel,
                filters: activeFilters,
                searchQuery: searchQuery
            })
        })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to create search.')
                }
            })
            .then(responseData => {
                console.log('Save search created')
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const [errorModal, setErrorModal]: any = useState({})
    const showErrorModal = (title?, message?) => {
        const element = document.getElementById('dashboard-error-modal') as HTMLDialogElement
        setErrorModal({
            title: title || strings.popupErrorTitle,
            message: message || strings.popupErrorContent
        })
        element.showModal()
    }

    const handleEditSearch = () => {
        fetch(`${endpoints.saveSearch}&id=${editSearch?.id}`, {
            method: 'put',
            body: JSON.stringify({
                label: editSearch?.label,
                filters: editSearch?.filters,
                searchQuery: editSearch?.searchQuery
            })
        })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to edit search.')
                }
            })
            .then(responseData => {
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const handleGetSearches = () => {
        fetch(endpoints.saveSearch)
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to get searches.')
                }
            })
            .then(responseData => {
                setSearches(responseData.contents)
            })
            .catch(error => {
                console.error('Fail while get searches')
            })
    }

    const handleSetSearch = (filters) => {
        clearActiveFilters()
        filters.forEach(f => {
            const checkbox = document.getElementById(f.id) as HTMLInputElement
            if (checkbox) {
                checkbox.checked = true
            }
        })
        setActiveFilters(filters)
    }

    const handleDeleteSearch = (id) => {
        fetch(`${endpoints.saveSearch}&id=${id}`, {
            method: 'delete'
        })
            .then(response => {
                if (response.ok) {
                    return response
                } else {
                    throw new Error('Failed to delete search.')
                }
            })
            .then(response => {
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    useEffect(() => {
        handleGetSearches()
    }, [])

    // Download CSV File
    const [isDownloading, setIsDownloading] = useState(false)
    const downloadCSVFile = () => {
        const queryParams = getQueryParams()
        const queryParamsObj = {}

        queryParams.forEach((value, key) => {
            if (queryParamsObj[key]) {
                if (Array.isArray(queryParamsObj[key])) {
                    queryParamsObj[key].push(value)
                } else {
                    queryParamsObj[key] = [queryParamsObj[key], value]
                }
            } else {
                queryParamsObj[key] = value
            }
        })

        sendWsMessage('download_csv', typePage, queryParamsObj)
        setIsDownloading(true)
    }

    const [ws, setWs] = useState(null)
    useEffect(() => {
        // Websocket connection
        const websocket = new WebSocket(endpoints.websocket);

        websocket.onopen = () => {
            console.log('Websocket connected');
        };

        websocket.onmessage = (message) => {
            try {
                const messageData = JSON.parse(message.data)

                if (messageData.csvContent) {
                    openDownloadCSVFile(messageData.csvContent)
                    setIsDownloading(false)
                }
            } catch (err) {
                console.error('Fail while reading ws message')
            }
        };

        websocket.onclose = () => {
            console.log('Websocket disconnected');
        };

        setWs(websocket);

        setInterval(() => {
            // Send Ping message
            websocket.send(JSON.stringify({
                type: 'ping'
            }));
        }, 30000)

        return () => {
            websocket.close();
        };
    }, []);

    const sendWsMessage = (type: 'ping' | 'download_csv', typePage?, params?) => {
        if (ws && (ws.readyState == 1)) {
            // Send WS message
            ws.send(JSON.stringify({
                type: type,
                typePage,
                params
            }));
        }
    };

    const openDownloadCSVFile = (csvContent) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const fileName = `${typePage}-${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.csv`

        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);

        // To Safari
        if (typeof link.download === 'undefined') {
            window.open(url);
        } else {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        URL.revokeObjectURL(url);
    }

    return (
        <main className='page-dashboard__main'>
            <div className='dashboard-grid-container'>

                <div className='dashboard-grid-container__left'>
                    <div className='sidebar'>
                        <Button
                            style='solid'
                            size='medium'
                            className='save-search'
                            onClick={() => {
                                (document.getElementById('save-search-textarea') as HTMLTextAreaElement).value = '';
                                (document.getElementById('lagre-sok') as HTMLDialogElement).showModal()
                            }}
                        >
                            {strings.saveSearchButton}
                        </Button>

                        <Popup id='lagre-sok' title={strings.saveSearchButton}>

                            <p className='dash-big'>
                                {strings.saveSearchCreateHeading}
                            </p>

                            <TextArea
                                id='save-search-textarea'
                                name='Lagre sok'
                                placeholder='Bringebær & lakris'
                                onChange={(e) => setSaveSearchLabel(e?.target?.value || '')}
                            />

                            {inputValue && (
                                <>
                                    <p className='dash-big'>
                                        {strings.searchedTerm}
                                    </p>

                                    <Input
                                        type='text'
                                        id='search-query'
                                        name='Searched term'
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </>
                            )}

                            <div className='filters'>
                                {activeFilters.map((filter, index) => (
                                    <button
                                        key={index}
                                        className='dash-tag'
                                        onClick={() => clearFilter(filter.id)}
                                    >

                                        <span>
                                            {filter.text}
                                        </span>

                                        <span className='clear'>
                                            <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                        </span>

                                    </button>
                                ))}
                            </div>

                            <div className='bottom-buttons'>

                                <Button
                                    style='hollow'
                                    size='medium'
                                    data-dialog-close
                                >
                                    {strings.saveSearchCancelButton}
                                </Button>

                                <Button
                                    style='solid'
                                    size='medium'
                                    data-dialog-close
                                    onClick={() => {
                                        handleSaveSearch()
                                    }}
                                >
                                    {strings.saveSearchConfirmButton}
                                </Button>

                            </div>

                        </Popup>

                        <div className={clsx(
                            'search-bar',
                            searchFocus && 'search-bar--has-focus'
                        )}>

                            <div className='search-bar-wrapper'>

                                <Input
                                    type='text'
                                    id='dash-search-input'
                                    name='Search'
                                    onFocus={() => setSearchFocus(true)}
                                    onBlur={() => setSearchFocus(false)}
                                    value={inputValue}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value)
                                        setInputValue(e.target.value)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleCards()
                                        }
                                    }}
                                />

                                <button className='button--search' onClick={() => handleCards()}>
                                    <svg width='23' height='23' viewBox='0 0 23 23' xmlns='http://www.w3.org/2000/svg'><path d='M6.57043 1.24854C7.76349 0.754353 9.04221 0.5 10.3336 0.5C11.6249 0.5 12.9037 0.754353 14.0967 1.24854C15.2898 1.74272 16.3738 2.46706 17.287 3.38019C18.2001 4.29332 18.9244 5.37736 19.4186 6.57043C19.9128 7.76349 20.1671 9.04221 20.1671 10.3336C20.1671 11.6249 19.9128 12.9037 19.4186 14.0967C18.9883 15.1356 18.3835 16.0917 17.6315 16.9244L22.3538 21.6467C22.5491 21.8419 22.5491 22.1585 22.3538 22.3538C22.1585 22.5491 21.8419 22.5491 21.6467 22.3538L16.9244 17.6315C15.1218 19.2597 12.7744 20.1671 10.3336 20.1671C7.72555 20.1671 5.22434 19.1311 3.38019 17.287C1.53603 15.4428 0.5 12.9416 0.5 10.3336C0.5 7.72555 1.53603 5.22434 3.38019 3.38019C4.29332 2.46706 5.37736 1.74272 6.57043 1.24854ZM10.3336 1.5C9.17353 1.5 8.02485 1.72849 6.95311 2.17242C5.88137 2.61634 4.90757 3.26702 4.08729 4.08729C2.43068 5.74391 1.5 7.99076 1.5 10.3336C1.5 12.6764 2.43068 14.9232 4.08729 16.5799C5.74391 18.2365 7.99076 19.1671 10.3336 19.1671C12.6764 19.1671 14.9232 18.2365 16.5799 16.5799C17.4001 15.7596 18.0508 14.7858 18.4947 13.714C18.9387 12.6423 19.1671 11.4936 19.1671 10.3336C19.1671 9.17353 18.9387 8.02485 18.4947 6.95311C18.0508 5.88137 17.4001 4.90757 16.5799 4.08729C15.7596 3.26702 14.7858 2.61634 13.714 2.17242C12.6423 1.72849 11.4936 1.5 10.3336 1.5Z' /></svg>
                                </button>

                            </div>

                            <div className='search-suggestions text-smaller'>
                                <div className='wrapper'>

                                    <ul className='list'>
                                        {(searches || []).map((item, i) => (
                                            <li key={i}>
                                                <button onClick={() => {
                                                    setSearchQuery(item.searchQuery || '')
                                                    setInputValue(item.searchQuery)
                                                    handleSetSearch(item.filters)
                                                    handleCards()
                                                    // TODO: This handle is not working in specific case
                                                }}>
                                                    <span className='circle'></span>
                                                    <span>{item.label}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    <a href='#lagrede-sok' data-dialog className='save-search'>
                                        <b>
                                            <u>
                                                {strings.saveSearchShowAll}
                                            </u>
                                        </b>
                                    </a>

                                    <Popup id='lagrede-sok' title={strings.saveSearchHeader}>
                                        <div className='inner-scroll'>
                                            {searches.map((item, i) => (
                                                <div className='saved-item' key={i}>
                                                    <div className='saved-item__top'>

                                                        <h3 className='h4'>
                                                            {item.label}
                                                        </h3>

                                                        <button className='exclude' onClick={() => (document.getElementById('exclude-' + i) as HTMLDialogElement).showModal()}>
                                                            <svg width='22' height='24' viewBox='0 0 22 24' xmlns='http://www.w3.org/2000/svg'><path d='M21.3125 4.09375H16.1562V2.6875C16.1562 2.0659 15.9093 1.46976 15.4698 1.03022C15.0302 0.59068 14.4341 0.34375 13.8125 0.34375H8.1875C7.5659 0.34375 6.96976 0.59068 6.53022 1.03022C6.09068 1.46976 5.84375 2.0659 5.84375 2.6875V4.09375H0.6875C0.56318 4.09375 0.443951 4.14314 0.356044 4.23104C0.268136 4.31895 0.21875 4.43818 0.21875 4.5625C0.21875 4.68682 0.268136 4.80605 0.356044 4.89396C0.443951 4.98186 0.56318 5.03125 0.6875 5.03125H2.09375V22.375C2.09375 22.748 2.24191 23.1056 2.50563 23.3694C2.76935 23.6331 3.12704 23.7812 3.5 23.7812H18.5C18.873 23.7812 19.2306 23.6331 19.4944 23.3694C19.7581 23.1056 19.9062 22.748 19.9062 22.375V5.03125H21.3125C21.4368 5.03125 21.556 4.98186 21.644 4.89396C21.7319 4.80605 21.7812 4.68682 21.7812 4.5625C21.7812 4.43818 21.7319 4.31895 21.644 4.23104C21.556 4.14314 21.4368 4.09375 21.3125 4.09375ZM6.78125 2.6875C6.78125 2.31454 6.92941 1.95685 7.19313 1.69313C7.45685 1.42941 7.81454 1.28125 8.1875 1.28125H13.8125C14.1855 1.28125 14.5431 1.42941 14.8069 1.69313C15.0706 1.95685 15.2188 2.31454 15.2188 2.6875V4.09375H6.78125V2.6875ZM18.9688 22.375C18.9688 22.4993 18.9194 22.6185 18.8315 22.7065C18.7435 22.7944 18.6243 22.8438 18.5 22.8438H3.5C3.37568 22.8438 3.25645 22.7944 3.16854 22.7065C3.08064 22.6185 3.03125 22.4993 3.03125 22.375V5.03125H18.9688V22.375ZM8.65625 10.1875V17.6875C8.65625 17.8118 8.60686 17.931 8.51896 18.019C8.43105 18.1069 8.31182 18.1562 8.1875 18.1562C8.06318 18.1562 7.94395 18.1069 7.85604 18.019C7.76814 17.931 7.71875 17.8118 7.71875 17.6875V10.1875C7.71875 10.0632 7.76814 9.94395 7.85604 9.85604C7.94395 9.76814 8.06318 9.71875 8.1875 9.71875C8.31182 9.71875 8.43105 9.76814 8.51896 9.85604C8.60686 9.94395 8.65625 10.0632 8.65625 10.1875ZM14.2812 10.1875V17.6875C14.2812 17.8118 14.2319 17.931 14.144 18.019C14.056 18.1069 13.9368 18.1562 13.8125 18.1562C13.6882 18.1562 13.569 18.1069 13.481 18.019C13.3931 17.931 13.3438 17.8118 13.3438 17.6875V10.1875C13.3438 10.0632 13.3931 9.94395 13.481 9.85604C13.569 9.76814 13.6882 9.71875 13.8125 9.71875C13.9368 9.71875 14.056 9.76814 14.144 9.85604C14.2319 9.94395 14.2812 10.0632 14.2812 10.1875Z' /></svg>
                                                        </button>

                                                        <Popup id={'exclude-' + i} title={strings.deleteSearchTitle}>

                                                            <p className='dash-big'>
                                                                {strings.deleteSearchContent}
                                                            </p>

                                                            <div className='bottom-buttons bottom-buttons--left'>

                                                                <Button
                                                                    style='hollow'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                    onClick={() => {
                                                                        handleDeleteSearch(item.id)
                                                                    }}
                                                                >
                                                                    {strings.deleteSearchConfirm}
                                                                </Button>

                                                                <Button
                                                                    style='solid'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                >
                                                                    {strings.deleteSearchCancel}
                                                                </Button>

                                                            </div>

                                                        </Popup>

                                                    </div>

                                                    <ul className='saved-item__filters'>
                                                        {item.filters.map((item, i) => (
                                                            <li key={i}>
                                                                <span className='dash-tag'>
                                                                    {item.text}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>

                                                    <div className='saved-item__bottom'>
                                                        <p>
                                                            {item.searchQuery ? (
                                                                <>
                                                                    {strings.searchedTerm}: "{item.searchQuery}"
                                                                </>
                                                            ) : null}
                                                        </p>

                                                        <Button
                                                            style='solid'
                                                            size='medium'
                                                            onClick={() => {
                                                                (document.getElementById('save-search-textarea-' + i) as HTMLTextAreaElement).value = item?.label || '';
                                                                (document.getElementById('endre-' + i) as HTMLDialogElement).showModal()
                                                                setEditSearch(item)
                                                            }}
                                                        >
                                                            {strings.editSearchButton}
                                                        </Button>

                                                        <Popup id={'endre-' + i} title={strings.editSearchTitle}>

                                                            <p className='dash-big'>
                                                                {strings.editSearchInputText}
                                                            </p>

                                                            <TextArea
                                                                id={'save-search-textarea-' + i}
                                                                name='Lagre sok'
                                                                placeholder='Bringebær & lakris'
                                                                defaultValue={editSearch?.label}
                                                                onChange={(e) => {
                                                                    setEditSearch({
                                                                        ...editSearch,
                                                                        label: e.target.value
                                                                    })
                                                                }}
                                                            />

                                                            <p className='dash-big'>
                                                                {strings.searchedTerm}
                                                            </p>

                                                            <input
                                                                className='dash-input'
                                                                type='text'
                                                                id='search-query'
                                                                name='Searched term'
                                                                defaultValue={item.searchQuery}
                                                                onChange={(e) => {
                                                                    setEditSearch({
                                                                        ...editSearch,
                                                                        searchQuery: e.target.value
                                                                    })
                                                                }}
                                                            />

                                                            <div className='filters'>

                                                                {
                                                                    editSearch?.filters && editSearch.filters.map((filter, index) =>
                                                                        <button
                                                                            key={index}
                                                                            className='dash-tag'
                                                                            onClick={(e) => {
                                                                                const button = e.target as Element
                                                                                const closestButton = button.closest('button')

                                                                                if (closestButton) {
                                                                                    closestButton.remove()
                                                                                }

                                                                                setEditSearch({
                                                                                    ...editSearch,
                                                                                    filters: editSearch.filters.filter(f => f.id != filter.id)
                                                                                })
                                                                            }}
                                                                        >

                                                                            <span>
                                                                                {filter.text}
                                                                            </span>

                                                                            <span className='clear'>
                                                                                <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                                                            </span>

                                                                        </button>
                                                                    )
                                                                }

                                                            </div>

                                                            <div className='bottom-buttons'>

                                                                <Button
                                                                    style='hollow'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                >
                                                                    {strings.editSearchCancel}
                                                                </Button>

                                                                <Button
                                                                    style='solid'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                    onClick={() => handleEditSearch()}
                                                                >
                                                                    {strings.editSearchConfirm}
                                                                </Button>

                                                            </div>

                                                        </Popup>
                                                    </div>

                                                </div>
                                            ))}
                                        </div>
                                    </Popup>

                                </div>
                            </div>

                        </div>

                        {activeFilters.length > 0 && (
                            <div className='active-filters'>

                                <button
                                    onClick={() => {
                                        // clear the active filters
                                        setActiveFilters([])

                                        // uncheck all checkboxes based on the filter IDs
                                        activeFilters.forEach((filter) => {
                                            const checkbox = document.getElementById(filter.id) as HTMLInputElement

                                            if (checkbox) {
                                                checkbox.checked = false
                                            }
                                        })
                                    }}
                                    className='dash-tag dash-tag--active'
                                >

                                    <span>
                                        {strings.clearAllFilters}
                                    </span>

                                    <span className='clear'>
                                        <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                    </span>

                                </button>

                                {activeFilters.map((filter, index) => (
                                    <button
                                        key={index}
                                        className='dash-tag'
                                        onClick={() => clearFilter(filter.id)}
                                    >

                                        <span>
                                            {filter.text}
                                        </span>

                                        <span className='clear'>
                                            <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                        </span>

                                    </button>
                                ))}

                            </div>
                        )}

                        {
                            filters.justShow && (filters.justShow.length > 0) &&
                            <div className='checkboxes'>

                                <h3 className='dash-h4'>
                                    {strings.justShowFilterHeader}
                                </h3>

                                {filters.justShow.map((item, i) => (
                                    <Checkbox
                                        key={i}
                                        id={item.id}
                                        name='just-show'
                                        text={item.text}
                                        onChange={(e) =>
                                            handleCheckboxChange(item.id, item.text, 'just-show', e.target.checked)
                                        }
                                    />
                                ))}

                            </div>
                        }

                        <div className='accordions'>

                            <Accordion title='Dato' className='date'>

                                {
                                    domLoaded &&
                                    <ThemeProvider theme={createTheme(nnNO)}>
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale='en-gb'
                                        >

                                            <p className='dash-small'>
                                                {strings.dateFilterFrom}
                                            </p>

                                            <div className='date-input'>

                                                <DatePicker
                                                    closeOnSelect={true}
                                                    disableFuture={true}
                                                    minDate={dayjs('2000-01-01')}
                                                    maxDate={dayjs()}
                                                    name='Date Start'
                                                    ref={dateStart}
                                                    value={filterDateStart}
                                                    onChange={(e: any) => {
                                                        setFilterDateStart(e || null)
                                                    }}
                                                />

                                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z' /></svg>

                                            </div>

                                            {filterDateStart && (
                                                <div className='clear'>
                                                    <Button
                                                        style='solid'
                                                        size='small'
                                                        onClick={() => setFilterDateStart(null)}
                                                    >
                                                        {strings.dateFilterClear}
                                                    </Button>
                                                </div>
                                            )}

                                            <p className='dash-small'>
                                                {strings.dateFilterTo}
                                            </p>

                                            <div className='date-input'>

                                                <DatePicker
                                                    closeOnSelect={true}
                                                    disableFuture={true}
                                                    minDate={dayjs('2000-01-01')}
                                                    maxDate={dayjs()}
                                                    name='Date End'
                                                    ref={dateEnd}
                                                    value={filterDateEnd}
                                                    onChange={(e: any) => {
                                                        setFilterDateEnd(e || null)
                                                    }}
                                                />

                                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z' /></svg>

                                            </div>

                                            {filterDateEnd && (
                                                <div className='clear'>
                                                    <Button
                                                        style='solid'
                                                        size='small'
                                                        onClick={() => setFilterDateEnd(null)}
                                                    >
                                                        {strings.dateFilterClear}
                                                    </Button>
                                                </div>
                                            )}

                                        </LocalizationProvider>
                                    </ThemeProvider>
                                }

                            </Accordion>

                            {
                                filters.status && (filters.status.length > 0) &&
                                <Accordion title={strings.statusFilterHeader}>
                                    {filters.status.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='status'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'status', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.packaging && (filters.packaging.length > 0) &&
                                <Accordion title={strings.packagingFilterHeader}>
                                    {filters.packaging.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='packaging'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'packaging', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.taste && (filters.taste.length > 0) &&
                                <Accordion title={strings.tasteFilterHeader}>
                                    {filters.taste.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='taste'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'taste', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>

                            }

                            {
                                filters.category && (filters.category.length > 0) &&
                                <Accordion title={strings.categoryFilterHeader}>
                                    {filters.category.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='category'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'category', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.region && (filters.region.length > 0) &&
                                <Accordion title={strings.regionFilterHeader}>
                                    {filters.region.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='region'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'region', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients1 && (filters.ingredients1.length > 0) &&
                                <Accordion title={filters.ingredients1Label}>
                                    {filters.ingredients1.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient1'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient1', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients2 && (filters.ingredients2.length > 0) &&
                                <Accordion title={filters.ingredients2Label}>
                                    {filters.ingredients2.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient2'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient2', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients3 && (filters.ingredients3.length > 0) &&
                                <Accordion title={filters.ingredients3Label}>
                                    {filters.ingredients3.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient3'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient3', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                        </div>

                    </div>
                </div>

                <div className='dashboard-grid-container__right'>
                    <div className='cards'>

                        {cards.map((item, i) => (
                            <Card
                                key={i}
                                title={item.title}
                                number={item.number}
                                percentage={item.percentage}
                                smallText={item.smallText}
                            />
                        ))}

                    </div>

                    <div className='bottom-buttons'>

                        <Button style='hollow' size='medium' disabled={isDownloading} className={clsx(isDownloading && 'loading')} onClick={() => downloadCSVFile()}>

                            {
                                isDownloading ? (
                                    <svg width='512' height='512' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".05" d="M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z"/><path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z"/></svg>
                                ) : (
                                    <svg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path d='M12.0002 17.9618L6.10183 12.0652L7.28183 10.8652L11.1668 14.7502V0.333496H12.8335V14.7502L16.7168 10.8668L17.8985 12.0652L12.0002 17.9618ZM3.02683 23.6668C2.25905 23.6668 1.6185 23.4102 1.10516 22.8968C0.591829 22.3835 0.334607 21.7424 0.333496 20.9735V16.9352H2.00016V20.9735C2.00016 21.2302 2.10683 21.4657 2.32016 21.6802C2.5335 21.8946 2.7685 22.0013 3.02516 22.0002H20.9752C21.2307 22.0002 21.4657 21.8935 21.6802 21.6802C21.8946 21.4668 22.0013 21.2313 22.0002 20.9735V16.9352H23.6668V20.9735C23.6668 21.7413 23.4102 22.3818 22.8968 22.8952C22.3835 23.4085 21.7424 23.6657 20.9735 23.6668H3.02683Z' /></svg>
                                )
                            }

                            <span>
                                CSV
                            </span>

                        </Button>

                    </div>

                </div>

            </div>
            <Popup id='dashboard-error-modal' title={errorModal?.title}>
                <p className='dash-big'>{errorModal?.message}</p>
                <div className='bottom-buttons bottom-buttons--left'>
                    <Button
                        style='solid'
                        size='medium'
                        data-dialog-close
                    >
                        {strings.deleteSearchCancel}
                    </Button>
                </div>
            </Popup>
        </main>
    )
}
